// VariableCard — renders a single leaf of the expression tree.
//
// Layout matches the mockup (Screen 2 onwards): bordered card with a path
// crumb at the top, a delete checkbox, and the existing input component
// (Range / MultiSelect / etc.) for the attribute.
//
// PR 2b-3: each card is both a DRAG SOURCE (EXPRESSION_LEAF, carrying its
// nodeId) AND a DROP TARGET (accepts VARIABLE_ATTRIBUTE from the dictionary
// and EXPRESSION_LEAF from another card in the same zone). Dropping on a
// card forms a fresh OR group with both leaves as children.

import { useDrag, useDrop } from 'react-dnd';
import dataDictionary from '../../assets/data-dictionary/dataDictionary.json';
import {
  canAddAttribute,
  getComponent,
} from '../../helpers/dataDictionary';
import { getZoneVariables } from '../../helpers/expressionTree';
import {
  ADD_ATTRIBUTE_AND_WRAP,
  ADD_MY_DATA_AND_WRAP,
  REMOVE_ATTRIBUTE,
  SET_ATTRIBUTE_STATE,
  TYPES,
  WRAP_IN_GROUP,
} from '../../reducers/builderReducer';
import DeleteCheckbox from '../inputs/DeleteCheckbox';
import type {
  MembershipResourceType,
  VariableState,
} from '../../types/dataDictionary';

const { VARIABLE_ATTRIBUTE, MY_DATA_ATTRIBUTE, EXPRESSION_LEAF } = TYPES;

// Default operator for newly-formed groups (per stakeholder decision).
const DEFAULT_GROUP_OP = 'or' as const;

export type VariableCardProps = {
  variable: VariableState;
  /** Stable client-side id of the leaf in the expression tree. */
  nodeId: string;
  zoneType: 'include' | 'exclude';
  /** Whole builder state (passed for cross-zone canDrop validation). */
  builderState: any;
  builderDispatch: (action: { type: string; payload?: any }) => void;
};

type LeafDragItem = {
  nodeId: string;
  zoneType: 'include' | 'exclude';
};

type DictDragItem = {
  id: string;
  name: string;
  allowedZoneTypes?: string;
};

type MyDataDragItem = {
  id: string;
  name: string;
  // ATAD-146: the shared type, so a new kind (e.g. 'model') can't be missed here.
  resourceType: MembershipResourceType;
};

/**
 * Derive a breadcrumb-style path from the attribute id, omitting the leaf
 * name itself. e.g. `DemoIQ___Geography___state` → `DemoIQ › Geography`.
 * Returns the empty string if the id has only a leaf segment.
 */
function pathCrumbFromId(id: string): string {
  const parts = id.split('___');
  if (parts.length <= 1) return '';
  return parts.slice(0, -1).join(' › ');
}

export default function VariableCard({
  variable,
  nodeId,
  zoneType,
  builderState,
  builderDispatch,
}: VariableCardProps) {
  const { Component, attrs } = getComponent(dataDictionary, variable.id);
  const crumb = pathCrumbFromId(variable.id);

  // ── Drag source: this card can be dragged onto another card or group. ──
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: EXPRESSION_LEAF,
      item: { nodeId, zoneType } as LeafDragItem,
      collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    }),
    [nodeId, zoneType],
  );

  // ── Drop target: accept dictionary attrs, sidebar audiences/files (PR 2c),
  //    and other cards in the same zone — all wrap into a fresh group. ──
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: [VARIABLE_ATTRIBUTE, MY_DATA_ATTRIBUTE, EXPRESSION_LEAF],
      drop: (
        item: DictDragItem | MyDataDragItem | LeafDragItem,
        monitor,
      ) => {
        const dragType = monitor.getItemType();
        if (dragType === VARIABLE_ATTRIBUTE) {
          const dict = item as DictDragItem;
          builderDispatch({
            type: ADD_ATTRIBUTE_AND_WRAP,
            payload: {
              id: dict.id,
              targetNodeId: nodeId,
              op: DEFAULT_GROUP_OP,
              zoneType,
            },
          });
          return;
        }
        if (dragType === MY_DATA_ATTRIBUTE) {
          const md = item as MyDataDragItem;
          builderDispatch({
            type: ADD_MY_DATA_AND_WRAP,
            payload: {
              id: md.id,
              name: md.name,
              type: md.resourceType,
              targetNodeId: nodeId,
              op: DEFAULT_GROUP_OP,
              zoneType,
            },
          });
          return;
        }
        if (dragType === EXPRESSION_LEAF) {
          const leaf = item as LeafDragItem;
          builderDispatch({
            type: WRAP_IN_GROUP,
            payload: {
              sourceNodeId: leaf.nodeId,
              targetNodeId: nodeId,
              op: DEFAULT_GROUP_OP,
              zoneType,
            },
          });
          return;
        }
      },
      canDrop: (item, monitor) => {
        const dragType = monitor.getItemType();
        if (dragType === VARIABLE_ATTRIBUTE) {
          const dict = item as DictDragItem;
          if (
            dict.allowedZoneTypes &&
            !dict.allowedZoneTypes.includes(zoneType)
          ) {
            return false;
          }
          const sameZone = getZoneVariables(builderState, zoneType);
          const otherZone = getZoneVariables(
            builderState,
            zoneType === 'include' ? 'exclude' : 'include',
          );
          return (
            canAddAttribute(sameZone, dict.id) &&
            canAddAttribute(otherZone, dict.id)
          );
        }
        if (dragType === MY_DATA_ATTRIBUTE) {
          // Audience/file always allowed (no allowedZoneTypes restriction);
          // duplicates handled by the reducer (createAudienceLeaf would just
          // make a second leaf — for now we accept that, mirroring how
          // multiple audiences could be added in the legacy myData arrays).
          return true;
        }
        if (dragType === EXPRESSION_LEAF) {
          const leaf = item as LeafDragItem;
          if (leaf.zoneType !== zoneType) return false;
          if (leaf.nodeId === nodeId) return false;
          return true;
        }
        return false;
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver({ shallow: true }),
        canDrop: !!monitor.canDrop(),
      }),
    }),
    [nodeId, zoneType, builderState],
  );

  const isHighlighted = isOver && canDrop;

  const handleRemove = () =>
    builderDispatch({
      type: REMOVE_ATTRIBUTE,
      payload: { id: variable.id, zoneType },
    });

  return (
    <div
      ref={(el) => {
        drag(el);
        drop(el);
      }}
      data-tour="variable-card"
      className={[
        'rounded-lg bg-white p-3 border transition-colors relative',
        isDragging ? 'opacity-50' : '',
        isHighlighted ? 'border-2 border-dashed border-teal-500' : 'border-gray-200',
      ].join(' ')}
    >
      {isHighlighted && (
        // Floating "drop to group" hint, matching the mockup Screen 3.
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] bg-teal-500 text-white px-2 py-0.5 rounded-full whitespace-nowrap">
          drop to group
        </div>
      )}
      {/* Top-right close button — primary remove control in PR 2b-5. */}
      <button
        type="button"
        onClick={handleRemove}
        aria-label={`Remove ${variable.attributeName}`}
        data-tour="variable-card-remove"
        title={`Remove ${variable.attributeName}`}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-base leading-none px-1.5 py-0.5 rounded hover:bg-gray-100"
      >
        ×
      </button>
      {crumb && (
        <div className="text-xs text-gray-500 mb-1 truncate uppercase tracking-wide pr-6">{crumb}</div>
      )}
      <div className="flex flex-row items-center w-full pr-6">
        {/* DeleteCheckbox kept as a visual indicator (and secondary remove
            target) — clicking still removes for users who learned PR 2b-2's
            interaction. The new top-right "×" is the primary control. */}
        <DeleteCheckbox className="mr-2" onClick={handleRemove} />
        <Component
          id={variable.id}
          name={variable.attributeName}
          attrs={attrs}
          initialState={variable.state}
          label={attrs.scaleLabel || undefined}
          setVariablesState={(state: any, config: any) =>
            builderDispatch({
              type: SET_ATTRIBUTE_STATE,
              payload: {
                id: variable.id,
                zoneType,
                state,
                loadingInitialState: config?.loadingInitialState,
              },
            })
          }
        />
      </div>
    </div>
  );
}
